import {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {signInWithEmailAndPassword} from "firebase/auth";
import {auth} from "../../firebaseApp.ts";
import {UnauthenticatedBackendApi} from "../../api/UnauthenticatedApi.ts";
import {StepOne, StepOneFormData} from "./OnboardingFormStepOne.tsx";
import {accountValidators} from "./OnboardingFormPage.tsx";
import {validatorChain} from "../../components/form/validation.ts";
import {FormTodoType} from "../../components/form/FormTodo.tsx";
import {FormError} from "../../components/form/FormError.tsx";
import {Logo} from "../../components/specific/Logo.tsx";
import LoadingFrame from "../../components/LoadingFrame.tsx";
import {isAbortError} from "../../lib/abortError.ts";

interface InvitationData {
    email: string
    business_name: string
    inviter_name: string
}

type PageState = 'loading' | 'form' | 'used' | 'invalid' | 'login_required'

const GENERIC_SERVER_ERROR = "Er is een onbekende fout opgetreden op de server. We zijn hiervan op de hoogte gesteld. Probeer het later opnieuw."
const INVALID_INVITATION_MESSAGE = "Deze uitnodigingslink is ongeldig of verlopen"
const USED_INVITATION_MESSAGE = "Deze uitnodiging is al gebruikt"
const LOGIN_REQUIRED_MESSAGE = "Je uitnodiging is geaccepteerd. Log in met je wachtwoord om verder te gaan."

export function InvitedSignupPage() {
    const {token} = useParams<{ token: string }>()
    const navigate = useNavigate()

    const [pageState, setPageState] = useState<PageState>('loading')
    const [invitation, setInvitation] = useState<InvitationData | null>(null)

    const [accountData, setAccountData] = useState<StepOneFormData>({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        repeat_password: '',
        phone_number: '',
    })
    const [formTodo, setFormTodo] = useState<Array<FormTodoType>>([])
    const [formError, setFormError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const formStart = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!token) {
            setPageState('invalid')
            return
        }

        const controller = new AbortController()

        async function loadInvitation() {
            const unauthenticatedApi = new UnauthenticatedBackendApi()

            try {
                const rawResponse = await unauthenticatedApi.get('onboarding/invitation', {token}, controller.signal)

                if (rawResponse.status === 410) {
                    setPageState('used')
                    return
                }

                if (!rawResponse.ok) {
                    setPageState('invalid')
                    return
                }

                const data = await rawResponse.json() as InvitationData
                setInvitation(data)
                setAccountData((prev) => ({...prev, email: data.email}))
                setPageState('form')
            } catch (error) {
                if (isAbortError(error))
                    return

                setPageState('invalid')
            }
        }

        void loadInvitation()

        return () => controller.abort()
    }, [token])

    function canContinue(): boolean {
        let issueFound = false
        const todos: Array<FormTodoType> = []

        for (const key of Object.keys(accountData)) {
            const todosForThisField = validatorChain<StepOneFormData>(key, accountData, accountValidators)

            if (todosForThisField.length > 0)
                issueFound = true

            todos.push(...todosForThisField)
        }

        setFormTodo(todos)
        if (issueFound) formStart.current?.scrollIntoView({behavior: "smooth", block: "start"})
        return !issueFound
    }

    async function handleSubmit() {
        if (isSubmitting) return
        if (!token || !invitation) return
        if (!canContinue()) return

        setFormError('')
        setIsSubmitting(true)

        try {
            const unauthenticatedApi = new UnauthenticatedBackendApi()
            const rawResponse = await unauthenticatedApi.post('onboarding/accept-invitation', {
                token,
                first_name: accountData.first_name,
                last_name: accountData.last_name,
                phone_number: accountData.phone_number,
                password: accountData.password,
                repeat_password: accountData.repeat_password,
            })

            if (rawResponse.status === 410) {
                setFormError(USED_INVITATION_MESSAGE)
                setIsSubmitting(false)
                return
            }

            if (rawResponse.status === 404) {
                setFormError(INVALID_INVITATION_MESSAGE)
                setIsSubmitting(false)
                return
            }

            if (rawResponse.status === 400) {
                const body = await rawResponse.json().catch(() => null)
                setFormError(body?.detail || body?.message || "Controleer je gegevens en probeer het opnieuw.")
                setIsSubmitting(false)
                return
            }

            if (!rawResponse.ok) {
                setFormError(GENERIC_SERVER_ERROR)
                setIsSubmitting(false)
                return
            }
        } catch (error) {
            setFormError(GENERIC_SERVER_ERROR)
            setIsSubmitting(false)
            return
        }

        try {
            await signInWithEmailAndPassword(auth, invitation.email, accountData.password)
            navigate('/', {replace: true})
        } catch (error) {
            setPageState('login_required')
        }
    }

    if (pageState === 'loading') {
        return <LoadingFrame isLoading={true}><span>Loading...</span></LoadingFrame>
    }

    if (pageState === 'used' || pageState === 'invalid') {
        const message = pageState === 'used' ? USED_INVITATION_MESSAGE : INVALID_INVITATION_MESSAGE

        return (
            <div className="min-h-screen pt-8 flex flex-col items-center justify-start mx-auto md:max-w-7xl px-4 sm:px-6 lg:px-8 pb-12">
                <header className="bg-white w-full flex justify-center mb-12">
                    <Logo/>
                </header>
                <main className="mx-auto w-full md:max-w-2xl text-center mt-16">
                    <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{message}</p>
                </main>
            </div>
        )
    }

    if (pageState === 'login_required') {
        return (
            <div className="min-h-screen pt-8 flex flex-col items-center justify-start mx-auto md:max-w-7xl px-4 sm:px-6 lg:px-8 pb-12">
                <header className="bg-white w-full flex justify-center mb-12">
                    <Logo/>
                </header>
                <main className="mx-auto w-full md:max-w-2xl text-center mt-16">
                    <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{LOGIN_REQUIRED_MESSAGE}</p>
                    <button
                        className="mt-8 inline-flex justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-70 transition"
                        onClick={() => navigate('/', {replace: true})}
                    >
                        Naar inloggen
                    </button>
                </main>
            </div>
        )
    }

    return (
        <div
            className="min-h-screen pt-8 flex flex-col items-center justify-start mx-auto md:max-w-7xl px-4 sm:px-6 lg:px-8 pb-12">
            <header className="bg-white w-full flex justify-center mb-12">
                <Logo/>
            </header>
            {formError && <div className="mb-8"><FormError error={formError}/></div>}
            <main className="mx-auto w-full md:max-w-2xl" ref={formStart}>
                {invitation?.inviter_name && invitation?.business_name && (
                    <p className="text-center text-gray-600 mb-8">
                        Je bent uitgenodigd door {invitation.inviter_name} om {invitation.business_name} te beheren op
                        PocketCFO.
                    </p>
                )}
                <div className="mx-auto max-w-2xl mb-8 md:mb-32 min-h-[70vh] md:min-h-[50em]">
                    <StepOne
                        onUpdateFunc={(formData: StepOneFormData) => {
                            setAccountData((prev: StepOneFormData) => ({...prev, ...formData}))
                        }}
                        formTodos={formTodo}
                        defaultFormData={accountData}
                        lockedEmail={invitation?.email}
                    />

                    <div>
                        <button
                            type="submit"
                            className="flex w-full mt-8 justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-primary hover:opacity-70 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isSubmitting}
                            onClick={() => void handleSubmit()}
                        >
                            Account aanmaken
                        </button>
                    </div>
                </div>
            </main>
        </div>
    )
}
